import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchCar, createLead } from "../api";
import { ChevronLeft, ChevronRight, Calendar, Gauge, Settings, Fuel, Cog, Palette, Phone, Check, PlayCircle } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { getYouTubeEmbed } from "../lib/youtube";

const CarDetail = () => {
  const { slug } = useParams();
  const [car, setCar] = useState(undefined);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const touchStartX = useRef(0);
  const { toast } = useToast();

  const imgCount = car && car.images ? car.images.length : 0;
  const next = () => setActive((a) => (imgCount ? (a + 1) % imgCount : 0));
  const prev = () => setActive((a) => (imgCount ? (a - 1 + imgCount) % imgCount : 0));

  useEffect(() => {
    setActive(0);
    fetchCar(slug)
      .then(setCar)
      .catch(() => setCar(null));
  }, [slug]);

  if (car === undefined) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-gray-500">Loading…</div>;
  }

  if (car === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif-display text-3xl gpm-text-green mb-4">Car not found</h1>
        <Link to="/cars-for-sale" className="gpm-text-gold underline">Back to all cars</Link>
      </div>
    );
  }

  const specs = [
    { icon: Calendar, label: "Year", value: car.year },
    { icon: Gauge, label: "Mileage", value: `${car.mileage} mi` },
    { icon: Settings, label: "Transmission", value: car.transmission },
    { icon: Fuel, label: "Fuel", value: car.fuel },
    { icon: Cog, label: "Engine", value: car.engine },
    { icon: Palette, label: "Colour", value: car.colour },
  ];

  const submit = async (e) => {
    e.preventDefault();
    try {
      await createLead({ type: "enquiry", ...form, carTitle: car.title, carId: car.id });
      toast({ title: "Enquiry sent", description: `Thank you ${form.name || ""}, we will be in touch about the ${car.title} shortly.` });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us directly.", variant: "destructive" });
    }
  };

  return (
    <div className="bg-white">
      <div className="gpm-bg-green">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <Link to="/cars-for-sale" className="inline-flex items-center gap-1 gpm-text-cream hover:gpm-text-gold transition-colors text-sm">
            <ChevronLeft size={16} /> Back to Cars for Sale
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
        {/* Gallery slider */}
        <div className="lg:col-span-3">
          <div
            className="relative aspect-[3/2] overflow-hidden bg-gray-100 group select-none"
            onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const dx = e.changedTouches[0].clientX - touchStartX.current;
              if (dx > 50) prev();
              else if (dx < -50) next();
            }}
          >
            <div
              className="flex h-full transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${active * 100}%)` }}
            >
              {car.images.map((img, i) => (
                <img key={i} src={img} alt={`${car.title} ${i + 1}`} className="w-full h-full object-cover shrink-0" draggable="false" />
              ))}
            </div>

            {car.images.length > 1 && (
              <>
                <button onClick={prev} aria-label="Previous photo" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/45 hover:bg-black/70 text-white rounded-full transition-all opacity-0 group-hover:opacity-100">
                  <ChevronLeft size={22} />
                </button>
                <button onClick={next} aria-label="Next photo" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/45 hover:bg-black/70 text-white rounded-full transition-all opacity-0 group-hover:opacity-100">
                  <ChevronRight size={22} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {car.images.map((_, i) => (
                    <button key={i} onClick={() => setActive(i)} aria-label={`Go to photo ${i + 1}`} className="h-2 rounded-full transition-all" style={{ width: i === active ? 22 : 8, background: i === active ? "var(--gpm-gold)" : "rgba(255,255,255,0.7)" }} />
                  ))}
                </div>
                <span className="absolute top-3 right-3 px-2.5 py-1 text-[11px] font-semibold bg-black/55 text-white rounded">
                  {active + 1} / {car.images.length}
                </span>
              </>
            )}
          </div>

          {car.images.length > 1 && (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 mt-3">
              {car.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className="aspect-[3/2] overflow-hidden border-2 transition-all"
                  style={{ borderColor: i === active ? "var(--gpm-gold)" : "transparent" }}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-2">
          <h1 className="font-serif-display text-3xl md:text-4xl font-bold gpm-text-green leading-tight">{car.title}</h1>
          <p className="text-gray-500 mt-3">{car.short}</p>
          <div className="font-serif-display text-4xl font-bold gpm-text-green my-6">{car.priceLabel}</div>

          <div className="grid grid-cols-2 gap-4 mb-8">
            {specs.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="flex items-center gap-3 p-3" style={{ backgroundColor: "#f4f2ea" }}>
                  <Icon size={18} className="gpm-text-gold" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">{s.label}</div>
                    <div className="text-sm font-semibold gpm-text-green">{s.value}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <a
            href="tel:07785355378"
            className="flex items-center justify-center gap-2 w-full py-4 gpm-text-cream text-sm font-semibold uppercase tracking-wider transition-transform hover:-translate-y-0.5"
            style={{ backgroundColor: "var(--gpm-green)" }}
          >
            <Phone size={17} /> Call to Enquire
          </a>
        </div>
      </div>

      {/* Description + Enquiry */}
      <div className="max-w-7xl mx-auto px-6 pb-16 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <h2 className="font-serif-display text-2xl font-bold gpm-text-green mb-3">Description</h2>
          <div className="gpm-divider mb-6" />
          <p className="text-gray-600 leading-relaxed mb-6">{car.description}</p>
          <ul className="space-y-2">
            {["Comprehensive service history", "HPI clear", "Part exchange welcome", "Nationwide delivery available"].map((f) => (
              <li key={f} className="flex items-center gap-2 text-gray-700 text-sm">
                <Check size={16} className="gpm-text-gold" /> {f}
              </li>
            ))}
          </ul>

          {getYouTubeEmbed(car.videoUrl) && (
            <div className="mt-10">
              <h2 className="flex items-center gap-2 font-serif-display text-2xl font-bold gpm-text-green mb-3">
                <PlayCircle size={22} className="gpm-text-gold" /> Video Walkaround
              </h2>
              <div className="gpm-divider mb-6" />
              <div className="relative w-full overflow-hidden border border-black/10" style={{ paddingTop: "56.25%" }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={getYouTubeEmbed(car.videoUrl)}
                  title={`${car.title} video walkaround`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="p-6" style={{ backgroundColor: "#f4f2ea" }}>
            <h3 className="font-serif-display text-xl font-bold gpm-text-green mb-4">Make an Enquiry</h3>
            <form onSubmit={submit} className="space-y-3">
              {[
                { k: "name", p: "Your name", t: "text" },
                { k: "email", p: "Email address", t: "email" },
                { k: "phone", p: "Phone number", t: "tel" },
              ].map((f) => (
                <input
                  key={f.k}
                  type={f.t}
                  required={f.k !== "phone"}
                  placeholder={f.p}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="w-full px-4 py-3 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm bg-white"
                />
              ))}
              <textarea
                rows={4}
                placeholder="Your message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm bg-white"
              />
              <button
                type="submit"
                className="w-full py-3 gpm-text-cream text-sm font-semibold uppercase tracking-wider transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: "var(--gpm-green)" }}
              >
                Send Enquiry
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetail;
